import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { 
  ChartBarIcon,
  PlusIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon
} from "@heroicons/react/24/outline";
import { ContractLines } from "../billing-plan/contract-lines";
import { BillingPlanItems } from "../billing-plan/billing-plan-items";
import { BillingAttachmentDialog } from "../billing-plan/billing-attachment-dialog";
import { RemainingAmountSettingsDialog } from "../billing-plan/remaining-amount-settings-dialog";
import { billingPlanItemsApi } from "../../../../api/billing_plan_items";
import { updateProject } from "../../../../api/project";
import { DocumentArrowDownIcon, Cog6ToothIcon } from "@heroicons/react/24/outline";

export const BillingPlanTab = ({ project, canEdit = true }) => {
  const { t } = useTranslation();
  const [billingData, setBillingData] = useState({
    contractSum: 0,
    billedAmount: 0,
    completedAmount: 0,
    remainingAmount: 0,
    progressPercentage: 0,
    canCreateInvoice: true,
    tripletexSyncDisabled: false
  });
  const [loading, setLoading] = useState(false);
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [exportingAttachment, setExportingAttachment] = useState(false);
  const [remainingAmountSettingsOpen, setRemainingAmountSettingsOpen] = useState(false);
  const [remainingAmountSettings, setRemainingAmountSettings] = useState({
    enabled: project?.remaining_amount_enabled || false,
    percentage: project?.remaining_amount_percentage || 10,
    base: project?.remaining_amount_base || "contract", // "contract" or "completed"
  });
  const [lastTotals, setLastTotals] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  // Load settings from project when component mounts or project changes
  useEffect(() => {
    if (project) {
      setRemainingAmountSettings({
        enabled: project.remaining_amount_enabled || false,
        percentage: project.remaining_amount_percentage || 10,
        base: project.remaining_amount_base || "contract",
      });
    }
  }, [project?.id]);

  const handleContractLinesUpdate = (totals) => {
    setLastTotals(totals); // Store totals for recalculation
    if (totals) {
      const contractSum = parseFloat(totals.contractSum) || 0;
      const completedSum = parseFloat(totals.completedSum) || 0;

      const progressPercentage =
        contractSum > 0 ? (completedSum / contractSum) * 100 : 0;

      // Innestående = beholdning (fra prosjektinnstillinger). Uten aktiv innstilling: 0.
      let calculatedRemainingAmount = 0;
      if (remainingAmountSettings.enabled) {
        const baseAmount =
          remainingAmountSettings.base === "contract" ? contractSum : completedSum;
        calculatedRemainingAmount =
          (baseAmount * (remainingAmountSettings.percentage || 10)) / 100;
      }

      setBillingData((prev) => ({
        ...prev,
        contractSum: isNaN(contractSum) ? 0 : contractSum,
        completedAmount: isNaN(completedSum) ? 0 : completedSum,
        remainingAmount: isNaN(calculatedRemainingAmount) ? 0 : calculatedRemainingAmount,
        progressPercentage: isNaN(progressPercentage) ? 0 : progressPercentage,
      }));
    } else {
      // Reset to zero if no totals provided
      setBillingData((prev) => ({
        ...prev,
        contractSum: 0,
        completedAmount: 0,
        remainingAmount: 0,
        progressPercentage: 0,
      }));
    }
  };

  const handleBillingPlanTotals = useCallback(({ billedSum = 0 } = {}) => {
    const v = parseFloat(billedSum) || 0;
    setBillingData((prev) => ({
      ...prev,
      billedAmount: Number.isFinite(v) ? v : 0,
    }));
  }, []);

  const handleSaveRemainingAmountSettings = async (settings) => {
    setSavingSettings(true);
    try {
      // Update project in backend
      const updatedProject = await updateProject({
        id: project.id,
        remaining_amount_enabled: settings.enabled,
        remaining_amount_percentage: settings.percentage,
        remaining_amount_base: settings.base,
      });

      // Update local state
      setRemainingAmountSettings(settings);
      setRemainingAmountSettingsOpen(false);
      
      // Recalculate remaining amount with new settings if we have totals
      if (lastTotals) {
        handleContractLinesUpdate(lastTotals);
      }

      // Optionally update parent project object if needed
      if (updatedProject && updatedProject.data) {
        // The parent component should refresh the project data
        // For now, we'll just update local state
      }
    } catch (error) {
      console.error("Error saving remaining amount settings:", error);
      // Show error message to user
      alert("Kunne ikke lagre innstillinger. Prøv igjen.");
    } finally {
      setSavingSettings(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('no-NO', {
      style: 'currency',
      currency: 'NOK'
    }).format(amount);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "billed":
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case "pending":
        return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />;
      default:
        return <DocumentTextIcon className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "billed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "billed":
        return "Fakturert";
      case "pending":
        return "Venter fakturering";
      default:
        return status;
    }
  };

  const handleExportAttachment = async (settings) => {
    setExportingAttachment(true);
    setAttachmentDialogOpen(false);
    try {
      // Convert boolean values to strings for API
      const apiSettings = {
        show_line_details: String(settings.show_line_details),
        show_unit_price: String(settings.show_unit_price),
        show_completed_quantity: String(settings.show_completed_quantity),
        show_progress_percentage: String(settings.show_progress_percentage),
        show_group_totals: String(settings.show_group_totals),
        show_only_groups: String(settings.show_only_groups),
        show_invoices: String(settings.show_invoices),
        show_total_contract: String(settings.show_total_contract),
        show_only_completed_total: String(settings.show_only_completed_total),
      };
      const response = await billingPlanItemsApi.exportBillingAttachment(project.id, apiSettings);
      // Create blob and download
      const blob = new Blob([response.data], {
        type: "application/pdf"
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `faktureringsvedlegg_${project.id}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting billing attachment:", error);
    } finally {
      setExportingAttachment(false);
    }
  };


  return (
    <div className="space-y-6">
      {/* Warning for Tripletex sync disabled */}
      {billingData.tripletexSyncDisabled && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
            <h3 className="text-yellow-800 font-medium">
              {t("project.tripletex_sync.disabled")}
            </h3>
          </div>
          <p className="text-yellow-700 text-sm mt-1">
            {t("project.tripletex_sync.reason")}
          </p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          {t("project.billing_plan.overview")}
        </h3>
        <div className="flex space-x-2">
          {canEdit ? (
          <button
            onClick={() => setRemainingAmountSettingsOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
            title={t("project.billing_plan.remaining_settings_title")}
          >
            <Cog6ToothIcon className="h-4 w-4 mr-2" />
            {t("project.billing_plan.settings")}
          </button>
          ) : null}
          <button
            onClick={() => setAttachmentDialogOpen(true)}
            disabled={exportingAttachment}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exportingAttachment ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                Laster...
              </>
            ) : (
              <>
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                Faktureringsvedlegg
              </>
            )}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t("project.billing_plan.contract_sum")}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(billingData.contractSum)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t("project.billing_plan.completed")}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(billingData.completedAmount)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t("project.billing_plan.billed_amount")}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(billingData.billedAmount)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t("project.billing_plan.remaining_amount")}
                    {remainingAmountSettings.enabled && (
                      <span className="ml-2 text-xs text-gray-400">
                        ({remainingAmountSettings.percentage}% av {remainingAmountSettings.base === "contract" ? "kontrakt" : "utført"})
                      </span>
                    )}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(billingData.remainingAmount)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t("project.billing_plan.progress")}
        </h3>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className="bg-blue-600 h-2.5 rounded-full" 
            style={{ width: `${Math.min(100, Math.max(0, billingData.progressPercentage || 0))}%` }}
          ></div>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          {typeof billingData.progressPercentage === 'number' ? billingData.progressPercentage.toFixed(1) : '0.0'}% utført
        </p>
      </div>

      {/* Billing Plan Items */}
      <BillingPlanItems
        project={project}
        contractSum={billingData.contractSum}
        completedSum={billingData.completedAmount}
        canEdit={canEdit}
        onRefresh={handleBillingPlanTotals}
      />

      {/* Contract Lines */}
      <ContractLines project={project} canEdit={canEdit} onRefresh={handleContractLinesUpdate} />

      <BillingAttachmentDialog
        open={attachmentDialogOpen}
        onClose={() => setAttachmentDialogOpen(false)}
        onExport={handleExportAttachment}
      />

      <RemainingAmountSettingsDialog
        open={remainingAmountSettingsOpen}
        onClose={() => setRemainingAmountSettingsOpen(false)}
        onSave={handleSaveRemainingAmountSettings}
        initialSettings={remainingAmountSettings}
      />
    </div>
  );
};

